import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field } from '@/nova/components';

import { IngredientSection } from '../../../art-style/components/IngredientSection';

/** The host of a link as a section summary shows it ("acme.com" for "https://acme.com/promo?x=1"); the text itself
 *  when it does not parse as a URL, '' when blank. Pure. */
export const linkHost = (url: string): string => {
  const raw = url.trim();
  if (!raw) return '';
  try {
    return new URL(raw).host || raw;
  } catch {
    return raw;
  }
};

export interface PlayableLinksSectionProps {
  /** The section's id (one per rail). */
  id: string;
  /** The document's own click-through (`landing_url`). */
  landingUrl: string;
  onLandingUrl: (url: string) => void;
  /** A mini-game's terms link (`offer.tc_url`); left out, the field is not shown (an HTML5 banner has none). */
  tcUrl?: string;
  onTcUrl?: (url: string) => void;
  locked: boolean;
  open: boolean;
  onToggle: () => void;
}

/**
 * A playable's raw click-through fields (HTML5 banners and mini-games), shown only behind the creatives dev flag
 * ("777devdebug"). An owner never edits them: the export and the hosted embed click through the version's tracking
 * link when it has one (the composer's `landingOverride`), and otherwise through the document's own `landing_url`,
 * which a new playable is minted with as the subject's page on the brand's casino (`subjectLandingUrl`); the service
 * falls back to that same page when it is blank.
 */
export const PlayableLinksSection = ({
  id,
  landingUrl,
  onLandingUrl,
  tcUrl,
  onTcUrl,
  locked,
  open,
  onToggle,
}: PlayableLinksSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <IngredientSection
      id={id}
      title={t('Links')}
      icon="globe"
      summary={linkHost(landingUrl) || t('Not set')}
      open={open}
      onToggle={onToggle}
    >
      <Field
        label={t('Landing page')}
        value={landingUrl}
        onChange={onLandingUrl}
        placeholder="https://"
        disabled={locked}
      />
      {onTcUrl ? (
        <Field
          label={t('Terms and conditions link')}
          value={tcUrl ?? ''}
          onChange={onTcUrl}
          placeholder="https://"
          disabled={locked}
        />
      ) : null}
    </IngredientSection>
  );
};

export default PlayableLinksSection;
